import { splitProps } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { twJoin, twMerge } from '@troon/tailwind-preset/merge';
import type { JSX, ParentProps } from 'solid-js';

type HeadingProps = JSX.HTMLAttributes<HTMLHeadingElement> & {
	as: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';
	size?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';
};

export function Heading(inputProps: HeadingProps) {
	const [custom, props] = splitProps(inputProps, ['as', 'size', 'class']);
	return (
		<Dynamic
			component={custom.as}
			{...props}
			class={twMerge(
				twJoin(
					'leading-tight font-semibold md:leading-snug',
					(custom.size ?? custom.as) === 'h1' && 'text-4xl md:text-5xl',
					(custom.size ?? custom.as) === 'h2' && 'text-3xl md:text-4xl',
					(custom.size ?? custom.as) === 'h3' && 'text-2xl md:text-3xl',
					(custom.size ?? custom.as) === 'h4' && 'text-xl md:text-2xl',
					(custom.size ?? custom.as) === 'h5' && 'text-lg md:text-xl',
					(custom.size ?? custom.as) === 'h6' && 'text-base md:text-lg',
				),
				custom.class,
			)}
		/>
	);
}

export function HGroup(props: ParentProps) {
	return <hgroup class="flex flex-col gap-4">{props.children}</hgroup>;
}

HGroup.Heading = function HGroupHeading(props: Omit<HeadingProps, 'size'> & { color?: 'current' | 'brand' }) {
	return (
		<Heading
			{...props}
			size="p"
			class={twJoin(
				'text-sm font-medium tracking-wide uppercase',
				props.color === 'brand' ? 'text-brand' : 'color-current',
			)}
		/>
	);
};

HGroup.Content = function HGroupContent(props: Omit<HeadingProps, 'as'>) {
	return <Heading as="p" {...props} />;
};
